import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnswerResult } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import {
    answerFact,
    CHART_SIZE,
    chartAnswer,
    ChartFact,
    ChartOperation,
    chartOperationFor,
    ChartProgress,
    EMPTY_PROGRESS,
    factId,
    isComplete,
    PickIndex,
    pickNext,
    problemFor,
    totalFacts,
} from "./chartGame.ts";
import { ChartPicker } from "./ChartPicker.tsx";
import { clearChartProgress, readChartProgress, writeChartProgress } from "./chartProgressStore.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface ChartGameProps {
    pick: PickIndex;
    /** The chart to open on; anything but addition opens multiplication. The player can switch. */
    operation?: Operations;
    /** Every answer, right or wrong, as shown ("7 x 8") - for the missed-facts store. */
    onAnswer(front: string, result: AnswerResult): void;
    /** The chart was just completed, and which chart it was. */
    onComplete(progress: ChartProgress, operation: ChartOperation): void;
}

const NUMBERS = Array.from({ length: CHART_SIZE }, (_, i) => i + 1);

/**
 * The fill-the-chart game (ADR 0054): one asked fact above a 10 x 10 chart
 * that fills as facts are answered, saved on this device. The chart is
 * addition or multiplication (ADR 0098 §2), each with its own saved progress.
 *
 * As on the quiz, Enter marks and moves on in the same render, the verdict
 * lands in a slot that is always there, and focus goes back to the answer box
 * after anything else is pressed - including tapping a square to choose it.
 */
export function ChartGame({ pick, operation: initial, onAnswer, onComplete }: ChartGameProps) {
    const t = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [chart, setChart] = useState<ChartOperation>(() => chartOperationFor(initial));
    const [progress, setProgress] = useState<ChartProgress>(EMPTY_PROGRESS);
    const [current, setCurrent] = useState<ChartFact | null>(null);
    const [typed, setTyped] = useState("");
    const [verdict, setVerdict] = useState<{ text: string; result: AnswerResult } | null>(null);
    const [justFilled, setJustFilled] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    // After mount, so the prerendered page is the same for every visitor.
    const loadChart = useCallback(
        (operation: ChartOperation) => {
            const saved = readChartProgress(operation);
            setChart(operation);
            setProgress(saved);
            setCurrent(pickNext(saved, pick, null, CHART_SIZE, operation));
            setTyped("");
            setVerdict(null);
            setJustFilled(null);
            setConfirming(false);
        },
        [pick]
    );

    useEffect(() => {
        loadChart(chartOperationFor(initial));
    }, [loadChart, initial]);

    const isAddition = chart === Operations.Addition;
    const complete = isComplete(progress);
    const filled = new Set(progress.filled);
    const tricky = new Set(progress.tricky);
    const currentId = current ? factId(current.a, current.b, chart) : null;
    const front = current ? cardFaces(problemFor(current, chart)).front : "";

    function focusInput() {
        inputRef.current?.focus();
    }

    function submitAnswer() {
        if (!current) {
            return;
        }
        const faces = cardFaces(problemFor(current, chart));
        const { progress: next, result } = answerFact(progress, current, typed, chart);
        const id = factId(current.a, current.b, chart);

        setTyped("");
        setVerdict({ text: `${faces.front} = ${faces.back}`, result });
        if (result !== "skipped") {
            onAnswer(faces.front, result);
            setProgress(next);
            writeChartProgress(next, chart);
        }
        setJustFilled(result === "correct" ? id : null);

        if (result === "correct" && isComplete(next)) {
            setCurrent(null);
            onComplete(next, chart);
        } else {
            setCurrent(pickNext(next, pick, id, CHART_SIZE, chart));
        }
    }

    function choose(a: number, b: number) {
        setCurrent({ a, b });
        setTyped("");
        focusInput();
    }

    function startOver() {
        clearChartProgress(chart);
        setProgress(EMPTY_PROGRESS);
        setVerdict(null);
        setJustFilled(null);
        setConfirming(false);
        setCurrent(pickNext(EMPTY_PROGRESS, pick, null, CHART_SIZE, chart));
        focusInput();
    }

    function cell(a: number, b: number) {
        const id = factId(a, b, chart);
        const answer = chartAnswer(a, b, chart);
        if (filled.has(id)) {
            return (
                <td
                    key={b}
                    className={`chartCell chartCell--filled${id === justFilled ? " chartCell--new" : ""}`}
                    aria-label={format(
                        isAddition ? t("flashCards.chartCellFilledAdditionAriaTemplate") : t("flashCards.chartCellFilledAriaTemplate"),
                        { a, b, answer }
                    )}
                >
                    {answer}
                </td>
            );
        }
        const trickyLabel = isAddition
            ? t("flashCards.chartCellTrickyAdditionAriaTemplate")
            : t("flashCards.chartCellTrickyAriaTemplate");
        const emptyLabel = isAddition
            ? t("flashCards.chartCellEmptyAdditionAriaTemplate")
            : t("flashCards.chartCellEmptyAriaTemplate");
        const isCurrent = current !== null && current.a === a && current.b === b;
        const isMirror = !isCurrent && id === currentId;
        const classes = [
            "chartCell",
            tricky.has(id) ? "chartCell--tricky" : "",
            isCurrent ? "chartCell--current" : "",
            isMirror ? "chartCell--mirror" : "",
        ]
            .filter(Boolean)
            .join(" ");
        return (
            <td key={b} className={classes}>
                <button
                    type="button"
                    className="chartCellButton"
                    onClick={() => choose(a, b)}
                    aria-current={isCurrent ? "true" : undefined}
                    aria-label={format(tricky.has(id) ? trickyLabel : emptyLabel, { a, b })}
                />
            </td>
        );
    }

    const total = totalFacts();

    return (
        <div className="chartGame">
            <ChartPicker
                value={chart}
                onChange={(operation) => {
                    loadChart(operation);
                    focusInput();
                }}
            />

            <div className="chartProgress">
                <span>{format(t("flashCards.chartProgressTemplate"), { filled: progress.filled.length, total })}</span>
                <span className="chartProgressBar" aria-hidden="true">
                    <span style={{ width: `${(progress.filled.length / total) * 100}%` }} />
                </span>
            </div>

            {complete ? (
                <div className="chartComplete">
                    <p className="chartCompleteTitle">{t("flashCards.chartComplete")}</p>
                    <p className="quizSummaryCounts">
                        {format(t("flashCards.chartCompleteDetailTemplate"), { total, wrong: progress.wrong })}
                    </p>
                </div>
            ) : (
                <form
                    className="quizCard chartAsk"
                    onSubmit={(e) => {
                        e.preventDefault();
                        submitAnswer();
                    }}
                >
                    <p className="quizProblem chartProblem">{front} =</p>
                    <div className="chartAnswerRow">
                        <input
                            ref={inputRef}
                            className={`quizInput chartInput${verdict ? ` quizInput--${verdict.result}` : ""}`}
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                            onKeyDown={(e) => {
                                // As on the quiz: implicit submission on Enter
                                // does not always fire.
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    submitAnswer();
                                }
                            }}
                            disabled={!current}
                            aria-label={format(t("flashCards.answerForAriaTemplate"), { front })}
                        />
                        <button type="submit" className="quizSubmit" disabled={!current}>
                            {t("flashCards.enterButton")}
                        </button>
                    </div>
                </form>
            )}

            {/* Always rendered, always the same height, as on the quiz. */}
            <p className={`quizVerdict${verdict ? ` quizVerdict--${verdict.result}` : ""}`} aria-live="polite">
                {verdict && (
                    <>
                        {verdict.result === "correct"
                            ? t("flashCards.correct")
                            : verdict.result === "skipped"
                            ? t("flashCards.skipped")
                            : t("flashCards.notQuite")}
                        {" · "}
                        {verdict.text}
                    </>
                )}
            </p>

            <table className="chartGrid">
                <caption className="chartCaption">{isAddition ? t("flashCards.chartGridCaptionAddition") : t("flashCards.chartGridCaption")}</caption>
                <thead>
                    <tr>
                        <th scope="col" aria-hidden="true">{isAddition ? "+" : "×"}</th>
                        {NUMBERS.map((b) => (
                            <th key={b} scope="col" className={current?.b === b ? "chartHeader--current" : undefined}>
                                {b}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {NUMBERS.map((a) => (
                        <tr key={a}>
                            <th scope="row" className={current?.a === a ? "chartHeader--current" : undefined}>
                                {a}
                            </th>
                            {NUMBERS.map((b) => cell(a, b))}
                        </tr>
                    ))}
                </tbody>
            </table>

            <p className="quizHint">{t("flashCards.chartHint")}</p>

            <div className="chartStartOver">
                {confirming ? (
                    <>
                        <span>{t("flashCards.chartConfirmStartOver")}</span>
                        <button type="button" className="quizFinish" onClick={startOver}>
                            {t("flashCards.chartConfirmYes")}
                        </button>
                        <button
                            type="button"
                            className="quizFinish"
                            onClick={() => {
                                setConfirming(false);
                                focusInput();
                            }}
                        >
                            {t("flashCards.chartConfirmNo")}
                        </button>
                    </>
                ) : (
                    progress.filled.length + progress.tricky.length > 0 && (
                        <button
                            type="button"
                            className="quizFinish"
                            // A finished chart has nothing left to lose.
                            onClick={() => (complete ? startOver() : setConfirming(true))}
                        >
                            {t("flashCards.chartStartOver")}
                        </button>
                    )
                )}
            </div>
        </div>
    );
}
